import { useMemo } from 'react';

// Splits a leaf/group url into { path, params } for comparison. Query params are
// compared order-independent because Rails path helpers and request.fullpath can
// serialize the same params in a different order.
const parseUrl = (url) => {
  const [path, search = ''] = String(url || '').split('?');
  const params = new URLSearchParams(search);
  const entries = Array.from(params.entries()).sort();
  return { path, key: entries.map(([k, v]) => `${k}=${v}`).join('&') };
};

const paramsEqual = (a, b) => a === b;

// Scores how well a candidate url matches the current location; higher wins. 4 = exact path and
// params, 3 = exact path with no params on the candidate, 2 = exact path but different params,
// 1 = current path nested under the candidate (a show/edit under its index).
const scoreMatch = (candidate, current) => {
  const c = parseUrl(candidate);
  if (c.path === current.path) {
    if (paramsEqual(c.key, current.key)) return 4;
    if (!c.key) return 3;
    return 2;
  }
  if (c.path && current.path.startsWith(`${c.path}/`)) return 1 + c.path.length / 1000;
  return 0;
};

// Given the server-built `sections` and the current location, resolve which group
// and leaf are active (for the rail highlight, breadcrumb, and auto-open accordion).
export default function useActiveNav(sections, currentPath, currentFullPath) {
  return useMemo(() => {
    const current = parseUrl(currentFullPath || currentPath);
    let best = {
      score: 0, groupId: null, groupName: null, groupUrl: null, leafName: null, leafUrl: null,
    };

    sections.forEach((section) => {
      section.groups.forEach((group) => {
        // Direct-link group (no leaves): the group url is the candidate.
        if (!group.leaves || group.leaves.length === 0) {
          const score = scoreMatch(group.url, current);
          if (score > best.score) {
            best = {
              score, groupId: group.id, groupName: group.name, groupUrl: group.url,
              leafName: group.name, leafUrl: group.url,
            };
          }
          return;
        }
        group.leaves.forEach((leaf) => {
          const score = scoreMatch(leaf.url, current);
          if (score > best.score) {
            best = {
              score, groupId: group.id, groupName: group.name, groupUrl: group.url || null,
              leafName: leaf.name, leafUrl: leaf.url,
            };
          }
        });
      });
    });

    return best;
  }, [sections, currentPath, currentFullPath]);
}
